import type { FilePreviewType } from './UploadReceiptCard';
import type { FC } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Skeleton, Stack, styled, Typography } from '@mui/material';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import {
  Box,
  Iconify,
  PdfFileIcon,
  usePdfObjectUrl,
} from '@pflegenavi/web-components';
import { useTheme } from '@mui/material/styles';
import { truncateFileNameIfNeeded } from './truncateFileNameIfNeeded';

interface ImagePreviewContainerProps {
  currentImageUrl: string;
  file: FilePreviewType;
  imageUrlToTypeMap: UrlTypeNameMapping;
  onEnlarge: () => void;
  /**
   * Render PDFs inline instead of showing the icon placeholder (ENG-2295).
   * Turn it off where the preview column is too narrow to read an A4 page -
   * there the placeholder plus the enlarge dialog is more useful.
   */
  inlinePdfPreview?: boolean;
}

export const ImagePreviewContainer: FC<ImagePreviewContainerProps> = ({
  currentImageUrl,
  imageUrlToTypeMap,
  file,
  onEnlarge,
  inlinePdfPreview = true,
}) => {
  const [imageHovered, setImageHovered] = useState(false);
  // In-place point-zoom toggle. The origin is kept separate from the zoomed
  // flag so zooming out shrinks back toward the same point it grew from;
  // resetting the origin together with the scale would make the zoom-out
  // animation pivot around the image center.
  const [pointZoomed, setPointZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });

  // Reset zoom when navigating to a different image; render-time reset so the
  // new image never paints zoomed. The stale origin is harmless at scale 1.
  const [prevImageUrl, setPrevImageUrl] = useState(currentImageUrl);
  if (prevImageUrl !== currentImageUrl) {
    setPrevImageUrl(currentImageUrl);
    setPointZoomed(false);
  }

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (pointZoomed) {
      setPointZoomed(false);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setZoomOrigin({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
    setPointZoomed(true);
  };

  const { t } = useTranslation();

  const disabledTemp = false;
  const isLoading = imageUrlToTypeMap.get(currentImageUrl) === undefined;

  const fileWithType = imageUrlToTypeMap.get(currentImageUrl);

  const isPdf = fileWithType?.type === 'application/pdf';
  const isImage = fileWithType?.type.startsWith('image/') ?? false;
  const fileName = fileWithType?.name ?? file?.name ?? 'unknown';
  // The blob was already fetched to sniff the content type, so rendering the
  // PDF from it costs no second download.
  const pdfUrl = usePdfObjectUrl(
    isPdf && inlinePdfPreview ? fileWithType?.data : undefined
  );

  return (
    <Stack
      sx={{
        flex: 1,
        minWidth: 340,
        minHeight: 140,
      }}
    >
      {isLoading && (
        <Skeleton variant="rectangular" width="100%" height={324} />
      )}
      {!isLoading && (
        <picture
          onMouseEnter={() => !disabledTemp && setImageHovered(true)}
          onMouseLeave={() => setImageHovered(false)}
          style={{
            position: 'relative',
            // A tall image is longer than the height this container is given,
            // so it scrolls here. PDFs never do - the inline viewer is sized to
            // 100% height and scrolls its own pages.
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'block',
            width: '100%',
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Where PDFs render inline the enlarge button lives in the thumbnail
              row for every file type, so it does not jump around as the user
              switches between a PDF and an image (ENG-2295). */}
          {!inlinePdfPreview && (
            <ImageActionsOverlay imageHovered={imageHovered}>
              <Stack
                direction="row"
                justifyContent="flex-end"
                p={1}
                sx={{ pointerEvents: 'auto' }}
              >
                <ImageActionButton onClick={onEnlarge}>
                  <Iconify icon="akar-icons:enlarge" fontSize={20} />
                </ImageActionButton>
              </Stack>
            </ImageActionsOverlay>
          )}

          <source
            src={
              typeof file === 'string'
                ? file
                : file
                ? file.preview ?? undefined
                : ''
            }
            style={{ zIndex: -1, borderRadius: 4 }}
          />
          {!isPdf && !isImage ? (
            <FileTypeNotSupportedCard
              text={t('common.file-type-not-supported', {
                fileType: fileWithType?.type,
              })}
            />
          ) : isPdf && !inlinePdfPreview ? (
            <PdfDownloadCard fileUrl={currentImageUrl} fileName={fileName} />
          ) : isPdf ? (
            // Rendered inline (ENG-2295) so the receipt can be read while
            // filling in the form, without opening the enlarge dialog first.
            // It fills the height its container is given rather than holding
            // A4 proportions, so the dialog's spare vertical space goes to the
            // receipt; the built-in viewer fits the page to whatever it gets.
            // The blob body can still be downloading after the type mapping
            // resolved, so the skeleton covers that gap; the children are the
            // <object> fallback for browsers that render no inline PDF.
            pdfUrl ? (
              <object
                type="application/pdf"
                data={pdfUrl}
                aria-label={fileName}
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  borderRadius: 4,
                }}
              >
                <PdfDownloadCard
                  fileUrl={currentImageUrl}
                  fileName={fileName}
                />
              </object>
            ) : (
              <Skeleton variant="rectangular" width="100%" height="100%" />
            )
          ) : (
            <img
              alt=""
              src={currentImageUrl}
              onClick={handleImageClick}
              style={{
                zIndex: -1,
                borderRadius: 4,
                width: '100%',
                cursor: pointZoomed ? 'zoom-out' : 'zoom-in',
                transform: pointZoomed ? 'scale(2)' : 'none',
                transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                transition: 'transform 0.2s ease',
              }}
            />
          )}
        </picture>
      )}
    </Stack>
  );
};

interface ImageActionsOverlayProps {
  imageHovered: boolean;
}

export const ImageActionButton = styled(IconButton)(({ theme }) =>
  theme.unstable_sx({
    borderRadius: 6,
    color: 'black',
    bgcolor: 'rgb(255,255,255, 0.5)',
    '&:hover': {
      bgcolor: 'rgb(255,255,255, 0.7)',
    },
  })
);

interface FileTypeNotSupportedCardProps {
  text: string;
}

const FileTypeNotSupportedCard: FC<FileTypeNotSupportedCardProps> = ({
  text,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        height: 213,
        backgroundColor: theme.palette.grey['200'],
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      <Typography
        width="50%"
        textAlign={'center'}
        sx={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          position: 'absolute',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

interface PdfDownloadCardProps {
  fileUrl: string;
  fileName: string;
}

const PdfDownloadCard: FC<PdfDownloadCardProps> = ({ fileUrl, fileName }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const cleanedFileName = truncateFileNameIfNeeded(fileName);

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      gap={2}
      sx={{
        backgroundColor: theme.palette.grey['200'],
        borderRadius: 2,
        width: '100%',
        height: 324,
      }}
    >
      <a href={fileUrl} target="_blank" rel="noreferrer">
        <img
          alt="Click to download PDF file"
          src={PdfFileIcon}
          style={{ height: 71, display: 'block' }}
        />
      </a>
      <Stack alignItems="center" gap={1}>
        <Typography color={theme.palette.grey['500']} variant="subtitle2">
          {t('actions.click-icon-pdf')}
        </Typography>
        <Typography color={theme.palette.grey['500']} variant="subtitle2">
          {t('file.file-name') + ': ' + cleanedFileName}
        </Typography>
      </Stack>
    </Stack>
  );
};

const ImageActionsOverlay = styled(Box)<ImageActionsOverlayProps>(
  ({ imageHovered }) =>
    ({ theme }) =>
      theme.unstable_sx({
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        backgroundColor: 'transparent',
        display: imageHovered ? 'flex' : 'none',
        flexDirection: 'column',
        alignItems: 'stretch',
        // Let clicks fall through to the image for point-zoom; the button
        // strip re-enables pointer events on itself.
        pointerEvents: 'none',
      })
);
